import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/lib/utils';

/**
 * Mid-century RadioGroup — Radix-backed single-select control with the
 * same boundary contract as the rest of the form primitives (charcoal
 * 1.5px ring, orange on focus, no layered shadow). Native
 * <input type="radio"> is not used because the dot mark and focus ring
 * are OS-owned and break the design language.
 *
 * Usage:
 *   <RadioGroup value={preset} onValueChange={setPreset}>
 *     {PRESETS.map(p => (
 *       <label key={p.value} className="flex items-center gap-2">
 *         <RadioGroupItem value={p.value} id={`preset-${p.value}`} />
 *         <span>{p.label}</span>
 *       </label>
 *     ))}
 *   </RadioGroup>
 */
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn('grid gap-2', className)} {...props} />
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      'aspect-square h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px] border-charcoal bg-cream',
      'transition-colors',
      'focus-visible:outline-none focus-visible:border-orange',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <span className="block h-[8px] w-[8px] rounded-full bg-charcoal" aria-hidden="true" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
